import { QRCodeSVG } from 'qrcode.react';
import { createPortal } from 'react-dom';
import type { TrackedOrder, OrderFile } from '../types';

interface PrintableOrderSheetProps {
  order: TrackedOrder;
  files: OrderFile[];
  appBaseUrl?: string;
}

// Format date for print
function formatPrintDate(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): string {
  if (!timestamp) return 'Unknown';

  let date: Date;
  if (typeof timestamp === 'object' && '_seconds' in timestamp) {
    date = new Date(timestamp._seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return 'Unknown';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Get the proof/mockup image URL
function getProofImageUrl(files: OrderFile[]): string | null {
  // Prefer proof or mockup files, then customer artwork
  const proofFile = files.find(f => (f.type === 'proof' || f.type === 'mockup') && (f.downloadUrl || f.externalUrl));
  if (proofFile) {
    return proofFile.downloadUrl || proofFile.externalUrl || null;
  }

  const artworkFile = files.find(f => f.type === 'customer_artwork' && (f.downloadUrl || f.externalUrl));
  if (artworkFile) {
    return artworkFile.downloadUrl || artworkFile.externalUrl || null;
  }

  return null;
}

export function PrintableOrderSheet({ order, files, appBaseUrl }: PrintableOrderSheetProps) {
  const proofImageUrl = getProofImageUrl(files);
  const orderUrl = `${appBaseUrl || window.location.origin}/orders/${order.id}`;

  const printContent = (
    <div id="print-production-sheet" className="print-production-sheet">
      <style>{`
        @media print {
          /* Hide the entire app */
          #root, #app, .Polaris-Frame, .Polaris-Page, [class*="Polaris"] {
            display: none !important;
            visibility: hidden !important;
          }

          /* Show only our print sheet */
          #print-production-sheet {
            display: block !important;
            visibility: visible !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0.5in !important;
            background: white !important;
            z-index: 999999 !important;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
            font-size: 12px !important;
            color: black !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          @page {
            margin: 0;
            size: letter portrait;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
          }

          .print-header {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-start !important;
            margin-bottom: 12px !important;
            border-bottom: 2px solid #000 !important;
            padding-bottom: 8px !important;
          }

          .print-order-number {
            font-size: 28px !important;
            font-weight: bold !important;
            margin: 0 !important;
          }

          .print-company-name {
            font-size: 12px !important;
            color: #666 !important;
            margin: 2px 0 0 0 !important;
          }

          .print-qr-section {
            text-align: center !important;
          }

          .print-qr-label {
            font-size: 9px !important;
            color: #666 !important;
            margin-top: 2px !important;
          }

          .print-customer-section {
            display: flex !important;
            justify-content: space-between !important;
            margin-bottom: 12px !important;
          }

          .print-customer-info h3, .print-date-info h3 {
            font-size: 11px !important;
            font-weight: 600 !important;
            margin: 0 0 4px 0 !important;
            text-transform: uppercase !important;
            color: #666 !important;
          }

          .print-customer-name {
            font-size: 16px !important;
            font-weight: 600 !important;
            margin: 0 0 2px 0 !important;
          }

          .print-customer-email {
            font-size: 12px !important;
            color: #333 !important;
            margin: 0 !important;
          }

          .print-date {
            font-size: 12px !important;
            color: #333 !important;
          }

          .print-proof-section {
            text-align: center !important;
            margin-bottom: 12px !important;
          }

          .print-proof-section h3 {
            font-size: 11px !important;
            font-weight: 600 !important;
            margin: 0 0 6px 0 !important;
            text-transform: uppercase !important;
            color: #666 !important;
          }

          .print-proof-image {
            max-width: 100% !important;
            max-height: 200px !important;
            border: 1px solid #ddd !important;
            border-radius: 4px !important;
          }

          .print-no-image {
            padding: 20px !important;
            background: #f4f6f8 !important;
            color: #666 !important;
            border-radius: 4px !important;
            font-size: 12px !important;
          }

          .print-items-section {
            margin-bottom: 12px !important;
          }

          .print-items-section h3 {
            font-size: 11px !important;
            font-weight: 600 !important;
            margin: 0 0 6px 0 !important;
            text-transform: uppercase !important;
            color: #666 !important;
          }

          .print-items-table {
            width: 100% !important;
            border-collapse: collapse !important;
          }

          .print-items-table th,
          .print-items-table td {
            border: 1px solid #ddd !important;
            padding: 6px 8px !important;
            text-align: left !important;
            font-size: 11px !important;
          }

          .print-items-table th {
            background: #f4f6f8 !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            font-size: 10px !important;
          }

          .print-items-table td.qty {
            text-align: center !important;
            font-weight: 600 !important;
            font-size: 13px !important;
          }

          .print-properties {
            font-size: 10px !important;
            color: #666 !important;
            margin-top: 2px !important;
          }

          .print-footer {
            margin-top: 16px !important;
            padding-top: 8px !important;
            border-top: 2px solid #000 !important;
            display: flex !important;
            justify-content: space-between !important;
            font-size: 9px !important;
            color: #666 !important;
          }
        }

        @media screen {
          .print-production-sheet {
            display: none !important;
          }
        }
      `}</style>

      {/* Header with Order Number and QR Code */}
      <div className="print-header">
        <div>
          <h1 className="print-order-number">Order {order.shopifyOrderName}</h1>
          <p className="print-company-name">Six Hats Supply - Production Sheet</p>
        </div>
        <div className="print-qr-section">
          <QRCodeSVG value={orderUrl} size={80} level="M" />
          <p className="print-qr-label">Scan for order details</p>
        </div>
      </div>

      {/* Customer Info and Date */}
      <div className="print-customer-section">
        <div className="print-customer-info">
          <h3>Customer</h3>
          <p className="print-customer-name">{order.customerName}</p>
          <p className="print-customer-email">{order.customerEmail}</p>
        </div>
        <div className="print-date-info">
          <h3>Order Date</h3>
          <p className="print-date">{formatPrintDate(order.shopifyCreatedAt)}</p>
        </div>
      </div>

      {/* Proof/Mockup Image */}
      <div className="print-proof-section">
        <h3>Proof / Mockup</h3>
        {proofImageUrl ? (
          <img
            src={proofImageUrl}
            alt="Order proof"
            className="print-proof-image"
          />
        ) : (
          <div className="print-no-image">No proof image available</div>
        )}
      </div>

      {/* Line Items Table */}
      <div className="print-items-section">
        <h3>Line Items</h3>
        <table className="print-items-table">
          <thead>
            <tr>
              <th style={{ width: '40%' }}>Product</th>
              <th style={{ width: '15%' }}>SKU</th>
              <th style={{ width: '15%' }}>Variant</th>
              <th style={{ width: '10%' }}>Qty</th>
              <th style={{ width: '20%' }}>Properties</th>
            </tr>
          </thead>
          <tbody>
            {order.lineItems.map((item, index) => (
              <tr key={index}>
                <td>{item.productTitle}</td>
                <td>{item.sku || '-'}</td>
                <td>{item.variantTitle || '-'}</td>
                <td className="qty">{item.quantity}</td>
                <td>
                  {item.properties.length > 0 ? (
                    <div className="print-properties">
                      {item.properties.map((p, i) => (
                        <div key={i}><strong>{p.name}:</strong> {p.value}</div>
                      ))}
                    </div>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="print-footer">
        <span>Order ID: {order.id}</span>
        <span>Printed: {new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );

  // Render via portal to document.body to escape Polaris/React tree
  return createPortal(printContent, document.body);
}

export default PrintableOrderSheet;
